import { useState } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@examsreactor/ui';

type Vendor = 'all' | 'azure' | 'aws';

interface CatalogEntry {
  code: string;
  vendor: 'azure' | 'aws';
  title: string;
  level: string;
  domains: number;
  status: 'launch_commitment';
  type: 'certification' | 'course';
  badge?: string;
  lastReviewDate: string;
}

const catalog: CatalogEntry[] = [
  {
    code: 'AZ-900',
    vendor: 'azure',
    title: 'Azure Fundamentals',
    level: 'Fundamentals',
    domains: 3,
    status: 'launch_commitment',
    type: 'certification',
    lastReviewDate: '2026-04-25',
  },
  {
    code: 'AZ-104',
    vendor: 'azure',
    title: 'Azure Administrator',
    level: 'Associate',
    domains: 5,
    status: 'launch_commitment',
    type: 'certification',
    lastReviewDate: '2026-04-25',
  },
  {
    code: 'AZ-305',
    vendor: 'azure',
    title: 'Azure Solutions Architect Expert',
    level: 'Expert',
    domains: 4,
    status: 'launch_commitment',
    type: 'certification',
    lastReviewDate: '2026-04-25',
  },
  {
    code: 'AZ-400',
    vendor: 'azure',
    title: 'DevOps Engineer Expert',
    level: 'Expert',
    domains: 5,
    status: 'launch_commitment',
    type: 'certification',
    lastReviewDate: '2026-04-25',
  },
  {
    code: 'CLF-C02',
    vendor: 'aws',
    title: 'AWS Cloud Practitioner',
    level: 'Fundamentals',
    domains: 4,
    status: 'launch_commitment',
    type: 'certification',
    lastReviewDate: '2026-04-25',
  },
  {
    code: 'SAA-C03',
    vendor: 'aws',
    title: 'AWS Solutions Architect Associate',
    level: 'Associate',
    domains: 4,
    status: 'launch_commitment',
    type: 'certification',
    lastReviewDate: '2026-04-25',
  },
  {
    code: 'AI-200T00-A',
    vendor: 'azure',
    title: 'Develop AI cloud solutions on Microsoft Azure',
    level: 'Intermediate',
    domains: 0,
    status: 'launch_commitment',
    type: 'course',
    badge: 'Curso oficial Microsoft Learn',
    lastReviewDate: '2026-04-25',
  },
];

const vendorLabels: Record<Vendor, string> = {
  all: 'Todos',
  azure: 'Azure',
  aws: 'AWS',
};

export function Catalog() {
  const [vendor, setVendor] = useState<Vendor>('all');
  const filtered = catalog.filter((c) => vendor === 'all' || c.vendor === vendor);

  return (
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
      <div className="text-center">
        <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">Catalogo de certificaciones</h1>
        <p className="mt-4 text-lg text-text-secondary">
          Compromiso de lanzamiento. Mas certificaciones llegan en proximas olas.
        </p>
      </div>

      <div className="mt-10 flex justify-center">
        <div className="inline-flex rounded-lg border border-border-subtle bg-bg-secondary p-1">
          {(Object.keys(vendorLabels) as Vendor[]).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setVendor(v)}
              className={cn(
                'rounded-md px-4 py-1.5 text-sm font-medium transition-colors',
                vendor === v ? 'bg-bg-primary text-text-primary shadow-sm' : 'text-text-secondary',
              )}
            >
              {vendorLabels[v]}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filtered.map((c) => (
          <Link
            key={c.code}
            to={`/certificaciones/${c.code}`}
            className={cn(
              'group card transition-all hover:-translate-y-0.5 hover:shadow-md',
              c.type === 'course'
                ? 'border-accent-secondary/50 hover:border-accent-secondary bg-accent-secondary/5'
                : 'hover:border-accent-primary',
            )}
          >
            <div className="flex items-baseline justify-between">
              <span
                className={cn(
                  'text-xs font-mono uppercase',
                  c.type === 'course' ? 'text-accent-secondary' : 'text-accent-primary',
                )}
              >
                {c.vendor}
              </span>
              <span className="text-xs text-text-tertiary">{c.level}</span>
            </div>

            {c.type === 'course' && c.badge && (
              <span className="mt-2 inline-flex items-center rounded-full bg-accent-secondary/10 px-2 py-0.5 text-xs font-semibold text-accent-secondary">
                {c.badge}
              </span>
            )}

            <h3 className="mt-3 text-lg font-bold">{c.code}</h3>
            <p className="mt-1 text-sm text-text-secondary">{c.title}</p>

            {c.type === 'certification' ? (
              <p className="mt-3 text-xs text-text-tertiary">{c.domains} dominios oficiales</p>
            ) : (
              <p className="mt-3 text-xs text-text-tertiary">Curso de apoyo (no simulable)</p>
            )}

            <div className="mt-4 flex items-center justify-between">
              <span className="text-xs text-text-tertiary">
                Banco actualizado: {c.lastReviewDate}
              </span>
              <span
                className={cn(
                  'inline-flex items-center gap-1 text-sm font-medium transition-all group-hover:gap-2',
                  c.type === 'course' ? 'text-accent-secondary' : 'text-accent-primary',
                )}
              >
                {c.type === 'course' ? 'Ver curso' : 'Ver detalle'}
                <span aria-hidden="true">→</span>
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
